import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Loader2, XCircle } from "lucide-react";
import { PAYMENT as P } from "@/constants/testIds";
import { getPaymentStatus } from "@/lib/checkout";

const MAX_POLLS = 8;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | pending | error

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    let cancelled = false;
    let attempts = 0;

    const poll = async () => {
      try {
        const data = await getPaymentStatus(sessionId);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          setState("paid");
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) {
          setState("error");
          return;
        }
        attempts += 1;
        if (attempts >= MAX_POLLS) {
          setState("pending");
          return;
        }
        setTimeout(poll, 2000);
      } catch (e) {
        if (cancelled) return;
        attempts += 1;
        if (attempts >= MAX_POLLS) {
          setState("error");
          return;
        }
        setTimeout(poll, 2000);
      }
    };
    poll();
    return () => {
      cancelled = true;
    };
    // sessionId is the only external input; MAX_POLLS is a module constant and
    // attempts/cancelled are intentionally effect-local to the polling loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div
      data-testid={P.successPage}
      className="font-body bg-cream min-h-screen flex items-center justify-center px-6 text-ink antialiased"
    >
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className="max-w-lg w-full text-center rounded-2xl border border-line bg-white p-10 sm:p-14 shadow-[0_20px_40px_rgb(0,0,0,0.06)]"
      >
        {state === "checking" && (
          <>
            <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-cream border border-line">
              <Loader2 className="h-6 w-6 text-clay animate-spin" aria-hidden="true" />
            </span>
            <h1 className="mt-7 font-heading text-3xl sm:text-4xl tracking-tight text-ink">
              confirming your spot…
            </h1>
            <p className="mt-4 text-base text-stone-text leading-relaxed">
              Hang tight for just a moment while we confirm your payment.
            </p>
          </>
        )}

        {state === "paid" && (
          <>
            <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-sage">
              <Check className="h-7 w-7 text-white" aria-hidden="true" />
            </span>
            <p className="mt-7 font-body text-xs font-semibold uppercase tracking-[0.22em] text-clay">
              THE CALLED CREATOR HOUSE
            </p>
            <h1 className="mt-4 font-heading text-3xl sm:text-4xl tracking-tight text-ink leading-tight">
              you're in.
            </h1>
            <p className="mt-4 text-base sm:text-lg text-stone-text leading-relaxed">
              Your spot in the 4-week live cohort is confirmed. Watch your email for the next steps and everything you need to get started.
            </p>
            <p className="mt-6 font-accent text-3xl text-clay">so excited to build with you.</p>
          </>
        )}

        {state === "pending" && (
          <>
            <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-cream border border-line">
              <Loader2 className="h-6 w-6 text-clay" aria-hidden="true" />
            </span>
            <h1 className="mt-7 font-heading text-3xl sm:text-4xl tracking-tight text-ink">
              payment processing
            </h1>
            <p className="mt-4 text-base text-stone-text leading-relaxed">
              Your payment is still being confirmed. This can take a moment — you'll receive an email as soon as it's complete.
            </p>
          </>
        )}

        {state === "error" && (
          <>
            <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-cream border border-line">
              <XCircle className="h-7 w-7 text-clay" aria-hidden="true" />
            </span>
            <h1 className="mt-7 font-heading text-3xl sm:text-4xl tracking-tight text-ink">
              we couldn't confirm this
            </h1>
            <p className="mt-4 text-base text-stone-text leading-relaxed">
              We weren't able to confirm your payment. If you were charged, please reach out and we'll sort it out right away.
            </p>
          </>
        )}

        <Link
          to="/creator-house"
          data-testid={P.successBackCta}
          className="mt-9 inline-flex items-center justify-center rounded-full bg-sage px-8 py-3.5 text-base font-semibold text-white transition-[transform,background-color] duration-300 hover:bg-sage-dark hover:scale-[1.03]"
        >
          Back to the Creator House
        </Link>
      </motion.div>
    </div>
  );
}
